import isOnline from 'is-online';
import EventBus from 'eventing-bus';
import React, { useEffect } from 'react';
import { createBrowserHistory } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';

import routes from './routes.js';
import Admin from './layouts/Admin.jsx';
import Modals from './components/Modals';
import Login from './pages/Auth/Login.js';
import Signup from './pages/Auth/Signup.js';
import { logout } from './store/Auth/index.js';
import AdminPanel from './pages/Admin/index.js';
import PrivateRoute from './store/PrivateRoute';
import ErrorModal from './components/ErrorModal';
import PhoneNumbers from './pages/Phone/index.js';
import Workflows from './pages/Workflows/index.js';
import Dashboard from './pages/Dashboard/index.js';
import SinglePage from './pages/SinglePage/index.js';
import Connections from './pages/Connections/index.js';
import Unsubscribe from './pages/Unsubscribe/index.js';
import { getBranding } from './store/Settings/index.js';
import AccessDenied from './pages/AccessDenied/index.js';
import ResetPassword from './pages/Auth/ResetPassword.js';
import PermissionError from './components/PermissionError';
import CampaignDetails from './pages/Campaign/campaign.js';
import ForgotPassword from './pages/Auth/ForgotPassword.js';
import SingleAgent from './pages/Agent/SingleAgent/index.js';
import CampaignAnalytics from './pages/Campaign/analytics.js';
import GlobalErrorHandler from './components/GlobalErrorHandler';
import JobProgressPanel from './components/JobProgress/index.js';
import BrandingProvider from './components/BrandingProvider.jsx';
import WorkflowDetail from './pages/Workflows/WorkflowDetail.js';
import WorkflowBuilder from './pages/Workflows/WorkflowBuilder.js';
import WorkflowVersions from './pages/Workflows/WorkflowVersions.js';
import ExecutionMonitor from './pages/Workflows/ExecutionMonitor.js';
import OAuthCallback from './components/Integration/OAuthCallback.js';
import WorkflowScheduler from './pages/Workflows/WorkflowScheduler.js';
import AdminNotificationPopup from './components/AdminNotificationPopup';
import ExitImpersonate from './pages/Admin/Workspaces/ExitImpersonate.js';
import AgencyPlansModal from './components/AgencyPlansModal/AgencyPlansModal.js';
import PaymentRequiredBanner from './components/AgencyPlansModal/PaymentRequiredBanner.js';

import './App.css'
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { setWorkspaceId } from './store/axios';
import { extractWorkspaceId } from './utils/workspaceUtils';
import websocketService from './services/websocket.service';

const hist = createBrowserHistory();

// Component to manage workflow-open class based on route
const RouteManager = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Manage workflow-open class based on URL
    const pathname = location.pathname;
    const isWorkflowEditRoute = pathname.includes('/workflows') && pathname.includes('/edit');

    if (isWorkflowEditRoute) document.body.classList.add('workflow-open');
    else document.body.classList.remove('workflow-open');

    // Cleanup on unmount
    return () => { document.body.classList.remove('workflow-open') };
  }, [location]);

  useEffect(() => {
    // pehle remove karo
    document.body.classList.remove("agent-open");

    // check route: agar /agents/ ke baad kuch bhi hai => detail page
    if (/^\/agents\/[^/]+$/.test(location.pathname)) document.body.classList.add("agent-open");
  }, [location]);

  useEffect(() => {
    // Remove campaign-open class first
    document.body.classList.remove("campaign-open");

    // Check route: if /campaigns/ followed by an ID (not just /campaigns)
    // Match patterns like /campaigns/123 or /workspaceId/campaigns/123
    const isCampaignDetailRoute = /\/campaigns\/[^/]+(\/analytics)?$/.test(location.pathname) && !location.pathname.endsWith('/campaigns');
    if (isCampaignDetailRoute) document.body.classList.add("campaign-open");
  }, [location]);

  return children;
};

export const App = () => {
  const dispatch = useDispatch()
  const { currentWorkspace, userDetails } = useSelector(state => state.Settings || {})
  const [showAgencyPlansModal, setShowAgencyPlansModal] = React.useState(false)
  const [showHideBillingModal, setShowHideBillingModal] = React.useState(false)
  const [showErrorModal, setShowErrorModal] = React.useState(false)
  const [modalError, setModalError] = React.useState({ message: '', status: null })
  const subscription = currentWorkspace?.currentSubscription

  // Check for payment requirement - show appropriate modal
  useEffect(() => {
    // Don't show payment modal if user needs to set password first
    const needsPasswordSetup = userDetails && userDetails.temp_password && userDetails.temp_password !== ''

    // Check if grace period has expired
    const gracePeriodEndsAt = subscription?.gracePeriodEndsAt
    const isGracePeriodExpired = gracePeriodEndsAt && new Date(gracePeriodEndsAt) < new Date()

    const shouldShowModal = subscription && subscription.isLocked && (subscription.lockReason === 'payment_required' || (subscription.lockReason === 'payment_failed' && isGracePeriodExpired))

    if (shouldShowModal && !needsPasswordSetup) {
      // Check if this is a hideBilling user (handle both boolean and string values)
      const isHideBilling = subscription?.hideBilling === true || subscription?.hideBilling === 'true'
      const isGracePeriodEnded = subscription.lockReason === 'payment_failed' && isGracePeriodExpired

      if (isHideBilling && isGracePeriodEnded) {
        // Show PermissionError modal for hideBilling users
        setShowAgencyPlansModal(false)
        setShowHideBillingModal(true)
      } else {
        // Show AgencyPlansModal for normal users
        setShowHideBillingModal(false)
        setShowAgencyPlansModal(true)
      }
    }
    else {
      // No modal should be shown
      setShowAgencyPlansModal(false)
      setShowHideBillingModal(false)
    }
  }, [subscription, userDetails])

  // Listen for password setup completion to show agency plans modal for new users
  useEffect(() => {
    const handlePasswordSetupCompleted = () => {
      setTimeout(() => {
        if (subscription && subscription.isLocked && subscription.lockReason === 'payment_required') {
          console.log('✅ Showing agency plans modal after password setup')
          setShowAgencyPlansModal(true)
        }
      }, 500)
    }

    const unsubscribe = EventBus.on('password:setup:completed', handlePasswordSetupCompleted)

    return () => {
      unsubscribe()
    }
  }, [subscription])

  // Listen for payment completion events
  useEffect(() => {
    const handlePaymentCompleted = (data) => {
      console.log('Payment completed:', data)
      // Hide modal and refresh workspace data
      setShowPaymentModal(false)
      // Trigger workspace data refresh
      if (window.location.reload) {
        setTimeout(() => window.location.reload(), 1000)
      }
    }

    // Subscribe to payment events
    const unsubscribePayment = EventBus.on('payment:completed', handlePaymentCompleted)

    // Listen for websocket payment events if connected
    if (websocketService.socket) {
      websocketService.socket.on('payment:completed', handlePaymentCompleted)
    }

    return () => {
      unsubscribePayment()
      if (websocketService.socket) {
        websocketService.socket.off('payment:completed', handlePaymentCompleted)
      }
    }
  }, [])

  useEffect(() => {
    // Fetch branding configuration on app initialization
    dispatch(getBranding());

    // Extract and set workspace ID from URL
    const workspaceId = extractWorkspaceId(window.location.pathname);
    if (workspaceId) { setWorkspaceId(workspaceId); }

    // Skip Redux actions during OAuth callback to prevent component remounting
    const isOAuthCallback = window.location.pathname.includes('/oauth') ||
      window.location.pathname.includes('/integration/') && window.location.pathname.includes('/success') ||
      window.location.search.includes('code=') && window.location.search.includes('state=');

    const errorHandler = (error) => toast.error(error);
    const successHandler = (message) => toast.success(message);
    const tokenExpiredHandler = () => {
      dispatch(logout());
      websocketService.disconnect();
    };

    // Handle modal errors - show in popup instead of toast
    const modalErrorHandler = (errorData) => {
      setModalError({
        message: errorData.message || 'An error occurred',
        status: errorData.status
      });
      setShowErrorModal(true);
    };

    const unsubscribeError = EventBus.on('error', errorHandler);
    const unsubscribeSuccess = EventBus.on('success', successHandler);
    const unsubscribeTokenExpired = EventBus.on('tokenExpired', tokenExpiredHandler);
    const unsubscribeModalError = EventBus.on('error:modal', modalErrorHandler);

    // Cleanup event listeners
    return () => {
      unsubscribeError();
      unsubscribeSuccess();
      unsubscribeTokenExpired();
      unsubscribeModalError();

      // Disconnect WebSocket on unmount
      websocketService.disconnect();
    };
  }, []);

  // Listen for route changes to update workspace ID
  useEffect(() => {
    const handleLocationChange = () => {
      const workspaceId = extractWorkspaceId(window.location.pathname);
      setWorkspaceId(workspaceId);
    };

    // Listen for browser navigation events
    window.addEventListener('popstate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  return (
    <div>
      <Router history={hist}>
        <RouteManager>
          <PaymentRequiredBanner />
          <BrandingProvider />
          <Modals />
          <AdminNotificationPopup />
          {userDetails && <JobProgressPanel />}
          <ExitImpersonate />
          <AgencyPlansModal isOpen={showAgencyPlansModal} toggle={() => setShowAgencyPlansModal(false)} />
          <PermissionError isOpen={showHideBillingModal} toggle={() => setShowHideBillingModal(false)} error={`Your subscription period has ended. Please contact your agency owner for assistance:<br/><br/><strong style="font-size: 16px; color: #120348;">${currentWorkspace?.agencyOwnerEmail || 'N/A'}</strong>`} hideClose={true} />
          <ErrorModal isOpen={showErrorModal} toggle={() => setShowErrorModal(false)} error={modalError.message} status={modalError.status} />
          <Switch>
            <Route exact path='/' component={props => <Login history {...props} />} />
            <Route exact path='/login' component={props => <Login history {...props} />} />
            <Route exact path='/signup' component={props => <Signup history {...props} />} />
            <Route exact path='/invite/:token' component={props => <Login history {...props} />} />
            <Route exact path='/magicLogin/:hash' component={props => <Login history {...props} />} />
            <Route exact path='/signup/:hash' component={props => <Signup history {...props} />} />
            <Route exact path='/access-denied' component={props => <AccessDenied history {...props} />} />
            <Route exact path='/reset-password' component={props => <ResetPassword history {...props} />} />
            <Route exact path='/forgot-password' component={props => <ForgotPassword history {...props} />} />

            {/* Unsubscribe Route (public - no auth) */}
            <Route exact path='/unsubscribe/:trackingId' component={props => <Unsubscribe {...props} />} />

            {/* OAuth Callback Routes — Gmail/Outlook email OAuth is no longer supported; this
                route now serves WhatsApp / Facebook / future providers via :provider. */}
            <Route exact path='/integration/:provider/success' component={props => <OAuthCallback {...props} />} />

            {/* Connections Routes - MUST be before generic routes for proper matching */}
            <PrivateRoute path='/connections/:tab?' component={props => <Admin {...props}><Connections {...props} /></Admin>} />
            <PrivateRoute path='/:workspaceId/connections/:tab?' component={props => <Admin {...props}><Connections {...props} /></Admin>} />

            {routes.filter(route => !route.skipRoute).map((route, index) => {
              const RouteComponent = props => <Admin {...props}><route.component {...props} /></Admin>;
              return <PrivateRoute exact={route.exact !== false} key={index} path={route['path']} component={RouteComponent} />;
            })}
            {/* Single page routes - both with and without workspace prefix */}
            <PrivateRoute exact path='/agents/:id' component={props => <Admin {...props}><SingleAgent {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agents/:id' component={props => <Admin {...props}><SingleAgent {...props} /></Admin>} />

            <PrivateRoute exact path='/phones/:id' component={props => <Admin {...props}><PhoneNumbers {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/phones/:id' component={props => <Admin {...props}><PhoneNumbers {...props} /></Admin>} />

            <PrivateRoute exact path='/campaigns/:id' component={props => <Admin {...props}><CampaignDetails {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/campaigns/:id' component={props => <Admin {...props}><CampaignDetails {...props} /></Admin>} />

            <PrivateRoute exact path='/campaigns/:id/analytics' component={props => <Admin {...props}><CampaignAnalytics {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/campaigns/:id/analytics' component={props => <Admin {...props}><CampaignAnalytics {...props} /></Admin>} />

            <PrivateRoute exact path='/deals/:id' component={props => <Admin {...props}><SinglePage key={`deal-${props.match.params.id}`} {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/deals/:id' component={props => <Admin {...props}><SinglePage key={`deal-${props.match.params.id}`} {...props} /></Admin>} />

            <PrivateRoute exact path='/contacts/:id' component={props => <Admin {...props}><SinglePage key={`contact-${props.match.params.id}`} {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/contacts/:id' component={props => <Admin {...props}><SinglePage key={`contact-${props.match.params.id}`} {...props} /></Admin>} />

            <PrivateRoute exact path='/companies/:id' component={props => <Admin {...props}><SinglePage key={`company-${props.match.params.id}`} {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/companies/:id' component={props => <Admin {...props}><SinglePage key={`company-${props.match.params.id}`} {...props} /></Admin>} />

            {/* Dashboard Routes with ID - both with and without workspace prefix */}
            <PrivateRoute exact path='/dashboard/:dashboardId' component={props => <Admin {...props}><Dashboard key={`dashboard-${props.match.params.dashboardId}`} {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/dashboard/:dashboardId' component={props => <Admin {...props}><Dashboard key={`dashboard-${props.match.params.dashboardId}`} {...props} /></Admin>} />

            {/* Workflow Routes - both with and without workspace prefix */}
            <PrivateRoute exact path='/agent/workflows' component={props => <Admin {...props}><Workflows {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agent/workflows' component={props => <Admin {...props}><Workflows {...props} /></Admin>} />

            <PrivateRoute exact path='/agent/workflows/edit' component={props => <Admin {...props}><WorkflowBuilder {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agent/workflows/edit' component={props => <Admin {...props}><WorkflowBuilder {...props} /></Admin>} />

            <PrivateRoute exact path='/agent/workflows/executions/:executionId' component={props => <Admin {...props}><ExecutionMonitor {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agent/workflows/executions/:executionId' component={props => <Admin {...props}><ExecutionMonitor {...props} /></Admin>} />

            <PrivateRoute exact path='/agent/workflows/:id' component={props => <Admin {...props}><WorkflowDetail {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agent/workflows/:id' component={props => <Admin {...props}><WorkflowDetail {...props} /></Admin>} />

            <PrivateRoute exact path='/agent/workflows/:id/edit' component={props => <Admin {...props}><WorkflowBuilder {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agent/workflows/:id/edit' component={props => <Admin {...props}><WorkflowBuilder {...props} /></Admin>} />

            <PrivateRoute exact path='/agent/workflows/:workflowId/versions' component={props => <Admin {...props}><WorkflowVersions {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agent/workflows/:workflowId/versions' component={props => <Admin {...props}><WorkflowVersions {...props} /></Admin>} />

            <PrivateRoute exact path='/agent/workflows/:workflowId/schedule' component={props => <Admin {...props}><WorkflowScheduler {...props} /></Admin>} />
            <PrivateRoute exact path='/:workspaceId/agent/workflows/:workflowId/schedule' component={props => <Admin {...props}><WorkflowScheduler {...props} /></Admin>} />

            {/* <AdminPanel /> */}

            <Redirect from='*' to='/' />
          </Switch>
        </RouteManager>
      </Router>

      <GlobalErrorHandler />

      <ToastContainer position="bottom-center" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick={false} rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light"
        icon={({ type }) => {
          switch (type) {
            case 'info':
              return (
                <svg stroke='yellow' xmlns="http://www.w3.org/2000/svg" className="stroke-indigo-400" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" strokeWidth="2" /> <path d="M12 16v-4" strokeWidth="2" /> <path d="M12 8h.01" strokeWidth="2" />
                </svg>
              );
            case 'error':
              return (
                <svg stroke='#e74d3c' xmlns="http://www.w3.org/2000/svg" className="stroke-red-500" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" strokeWidth="2" /> <line x1="15" y1="9" x2="9" y2="15" strokeWidth="2" /> <line x1="9" y1="9" x2="15" y2="15" strokeWidth="2" />
                </svg>
              );
            case 'success':
              return (
                <svg fill='#01A601' version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 117.72 117.72"><g><path fill='#01A601' class="st0" d="M58.86,0c9.13,0,17.77,2.08,25.49,5.79c-3.16,2.5-6.09,4.9-8.82,7.21c-5.2-1.89-10.81-2.92-16.66-2.92 c-13.47,0-25.67,5.46-34.49,14.29c-8.83,8.83-14.29,21.02-14.29,34.49c0,13.47,5.46,25.66,14.29,34.49 c8.83,8.83,21.02,14.29,34.49,14.29s25.67-5.46,34.49-14.29c8.83-8.83,14.29-21.02,14.29-34.49c0-3.2-0.31-6.34-0.9-9.37 c2.53-3.3,5.12-6.59,7.77-9.85c2.08,6.02,3.21,12.49,3.21,19.22c0,16.25-6.59,30.97-17.24,41.62 c-10.65,10.65-25.37,17.24-41.62,17.24c-16.25,0-30.97-6.59-41.62-17.24C6.59,89.83,0,75.11,0,58.86 c0-16.25,6.59-30.97,17.24-41.62S42.61,0,58.86,0L58.86,0z M31.44,49.19L45.8,49l1.07,0.28c2.9,1.67,5.63,3.58,8.18,5.74 c1.84,1.56,3.6,3.26,5.27,5.1c5.15-8.29,10.64-15.9,16.44-22.9c6.35-7.67,13.09-14.63,20.17-20.98l1.4-0.54H114l-3.16,3.51 C101.13,30,92.32,41.15,84.36,52.65C76.4,64.16,69.28,76.04,62.95,88.27l-1.97,3.8l-1.81-3.87c-3.34-7.17-7.34-13.75-12.11-19.63 c-4.77-5.88-10.32-11.1-16.79-15.54L31.44,49.19L31.44,49.19z" /></g></svg>
              );
            case 'warning':
              return (
                <svg stroke='orange' xmlns="http://www.w3.org/2000/svg" className="stroke-yellow-500" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M10.29 3.86L1.82 18a1.64 1.64 0 001.41 2.5h16.54a1.64 1.64 0 001.41-2.5L13.71 3.86a1.65 1.65 0 00-2.83 0z" strokeWidth="2" /> <line x1="12" y1="9" x2="12" y2="13" strokeWidth="2" /> <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2" />
                </svg>
              );
            default:
              return null;
          }
        }}
      />
    </div>
  )
}

export default App;